/*
MIT License

Copyright (c) 2023 Artem Churyukin

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

const _templates=new Map();
const _helpers=new Map([['_',_=>_]]);

const _templates_get=(name)=>_templates.get(name);
const _helpers_get=(name)=>_helpers.get(name);

function _add_template(uid,str){
	if(_templates.has(uid)) return _templates.get(uid);
	const func=_compile(str);
	
	_templates.set(uid,func);
	
	return func;
}

function _render(str,data){
	const template = _templates.get(str);
	if(template) return template(data);
	const compiled = _compile(str);
	if (typeof compiled !== 'function') {
		throw new Error(`Failed to compile template: ${str}`);
	}
	return compiled(data);
}

function _compile(str){
	const compiled=`((_t,_h,_={})=>(${_parse(str).replace(/\t|\n/g,'')}))`;
	try{
		return eval(compiled).bind(this,_templates_get,_helpers_get);
	} catch(e){
		console.log(compiled);
		console.error(e);
		return () => {
			throw new Error(`Template compilation failed: ${e.message}`);
		};
	}
}

function _test(str){
	return str.indexOf("{{")!==-1;
}

function _add_helper(uid,func){
	_helpers.set(uid,func);
}

function _get_end(str,level=2,opener='{',closer='}'){
	for(let i=0;i<str.length;i++){
		if(level===0) return i;
		if(str.substr(i,opener.length)===opener){
			level++;
			i+=opener.length-1;
		}
		if(str.substr(i,closer.length)===closer){
			level--;
			i+=closer.length-1;
		}
	}
	
	return str.length;
}

const blocks=[
	{open:'"',close:'"'},
	{open:"'",close:"'"},
	{open:'`',close:'`'},
	{open:'(',close:')'},
	{open:'{',close:'}'},
	{open:'[',close:']'},
];

function _parse_arguments(str,{reference_level=0}){
	str=str.trim();
	
	const args=[''];
	
	const levels=[];
	
	for(let i=0;i<str.length;i++){
		//Either it's a closer for the level
		if(levels.length && str[i]===levels[levels.length-1].close){
			args[args.length-1]+=str[i];
			levels.pop();
		} else {
			let new_level;
			blocks.forEach(block=>{
				if(str[i]===block.open) new_level=block;
			});
			if(new_level){
				levels.push(new_level);
				args[args.length-1]+=str[i];
			} else if(str[i]===' '){
				if(levels.length===0){
					args.push('');
				} else {
					args[args.length-1]+=str[i];
				}
			} else {
				args[args.length-1]+=str[i];
			}
		}
	}
	
	return args.filter(el=>!!el).map(el=>_parse_argument(el.trim(),{reference_level}));
}

const _bool=new Set(['true','false']);

function _parse_reference(data,{f,s=true,str,reference_level=0}={}){
	data=data.trim();
	if(/^-?\d+(\.\d+)?$/.test(data)){
		return data;
	} else if(_bool.has(data.toLowerCase())){
		return data;
	} else {
		return `${f?'${':''}${_generate_reference(data,reference_level)}${str?'?.toString()':''}${s?'||""':''}${f?'}':''}`;
	}
}

function _generate_reference(data,level){
	//NOTE: replace(/\./g, '?.') replaces ALL dots, which could break if reference contains numeric literals
	//Edge case: if somehow a number gets passed, 1.5 becomes 1?.5 (invalid)
	//Generally safe since it's only called on reference paths, but risky
	data=data.replace(/\./g,'?.').replace(/`/g,'\\`');
	if(level===0) return `_.${data}`;
	let current='_';
	const refs=[`${current}.${data}`];
	for(let i=0;i<level;i++){
		current+='_';
		refs.push(`${current}.${data}`);
	}
	return `(${refs.reverse().map(ref=>`${ref}!==undefined?${ref}:`).join('')}undefined)`;
	//return `(${refs.reverse().join('||')})`;
}

function _parse_argument(arg,{reference_level=0}){
	if(arg[0]==='('){
		
		let str=arg.substr(1,arg.length-2);
		
		while(str[0]===' ') str=str.substr(1);
		
		let potential_helper=str.split(' ')[0];
		
		if(_helpers.has(potential_helper.trim())){
			const helper=potential_helper.trim();
			
			//TODO parse args char by char to avoid spaces inside JSON's or helper's calls.
			const args=_parse_arguments(str.substr(potential_helper.length).trim(),{reference_level});
			
			//NOTE: String escaping is inconsistent - backticks are escaped here but not consistently everywhere
			//This could cause issues if template literals in generated code contain backticks
			return `_h("${helper}")(${args.join(',').replace(/`/g,'\\`')})||""`;
		} else {
			return _parse_reference(potential_helper.trim(),{reference_level});
		}
	} else {
		let new_level;
		blocks.forEach(block=>{
			if(arg[0]===block.open) new_level=block;
		});
		if(!new_level){
			return _parse_reference(arg,{reference_level});
		}
	}
	
	return arg;
}

const string_chars = new Set(['"', "'", '`']);

function _parse_json(json,{reference_level=0}){
	for(let i=0;i<json.length;i++){
		if(string_chars.has(json[i])) {
			const next = json.indexOf(json[i], i + 1);
			if(next !== -1) i = next;
		} else if(json[i]==='('){
			const end=_get_end(json.substr(i+1),1,'(',')');
			const parsed=_parse_argument(json.substr(i,end+1),{reference_level});
			json=json.slice(0,i)+parsed+json.slice(i+end+1);
			i+=parsed.length;
		}
	}
	
	return json;
}

function _find_else(str){
	let next_if=str.indexOf("{{#if");
	let next_else=str.indexOf("{{else}}");
	
	if(next_else===-1) return -1;

	if(next_if===-1 || next_if>next_else) return next_else;
	
	let index=0;
	
	let rest=str;
	
	const max_iterations = 1000;
	let iterations = 0;
	
	while(iterations < max_iterations){
		iterations++;
		index+=next_if+5;
		rest=rest.substr(next_if+5);
		let end=_get_end(rest,1,'{{#if','{{/if}}');
		index+=end;
		rest=rest.substr(end);
		
		next_if=rest.indexOf("{{#if");
		next_else=rest.indexOf("{{else}}");
		
		if(next_else===-1) return -1;

		if(next_if===-1 || next_if>next_else) return index+next_else;
	}
	
	throw new Error('Maximum iterations reached in _find_else - malformed template');
}

function _parse_if_body(str="",{reference_level=0}){
	let else_index=_find_else(str);
	if(else_index===-1) return `${_parse(str,{reference_level})}:""`;
	
	return `${_parse(str.slice(0,else_index),{reference_level})}:${_parse(str.slice(else_index+8),{reference_level})}`;
}

function _parse(str='', {reference_level = 0} = {}){
	let rest=str;
	
	let res=`\``;
	
	let next_expr=rest.indexOf('{{');
	
	while(next_expr!=-1){
		if(next_expr>0) res+=rest.slice(0,next_expr);
		
		rest=rest.slice(next_expr+2);
		
		let end=_get_end(rest);
		let body=rest.slice(0,end-2).trim();
		rest=rest.slice(end);
		
		if(body[0]==='#'){
			body=body.slice(1).trimStart();
			
			let potential_complex=body.split(' ')[0];

			switch(potential_complex.trim()){
				case 'if':
					let if_end=_get_end(rest,1,'{{#if','{{/if}}');
					res+=`\${${_parse_reference(body.slice(potential_complex.length).trim(),{s:false, reference_level})}?${_parse_if_body(rest.slice(0,if_end-7),{reference_level})}}`;
					rest=rest.slice(if_end);
				break;
				case 'each':
					let each_end=_get_end(rest,1,'{{#each','{{/each}}');
					let ref='_';
					for(let i=0;i<reference_level+1;i++) ref+='_';
					res+=`\${(${_parse_reference(body.slice(potential_complex.length).trim(),{s:false,reference_level})}||[]).map((${ref}={})=>(${_parse(rest.slice(0,each_end-9),{reference_level:reference_level+1})})).join('')}`;
					rest=rest.slice(each_end);
				break;
			}
			
		} else if(body[0]==='>'){
			body=body.slice(1).trimStart();
			let potential_template=body.split(' ')[0];
			
			const template=potential_template.trim();
			
			const args=_parse_json(body.slice(potential_template.length).trim(),{reference_level});
			
			res+=`\${_t("${template}")(${args.replace(/`/g,'\\`')})}`;
			
		} else {
			//Helper or just output.
			let potential_helper=body.split(' ')[0];
			
			if(_helpers.has(potential_helper.trim())){
				const helper=potential_helper.trim();
				
				const args=_parse_arguments(body.slice(potential_helper.length).trim(),{reference_level});
				
				//NOTE: String escaping is inconsistent - backticks are escaped here but not consistently everywhere
				//This could cause issues if template literals in generated code contain backticks
				res+=`\${_h("${helper}")(${args.join(',').replace(/`/g,'\\`')})||""}`;
			} else {
				res+=_parse_reference(body,{f:true,str:true,reference_level});
			}
		}
		
		
		next_expr=rest.indexOf('{{');
	}
	
	res+=`${rest}\``;
	
	return res;
}

export default {
	templates:{
		add:_add_template,
		compile:_compile,
		render:_render,
		test:_test,
	},
	helpers:{
		add:_add_helper
	}
};
